import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  lazy,
  Suspense,
} from 'react';
import { registerProfileCompleteHandler } from '../utils/profileCompleteGate';
import { isUserProfileComplete } from '../utils/userProfileComplete';

const CompleteProfileModal = lazy(() =>
  import('../components/CompleteProfileModal/CompleteProfileModal'),
);

const CompleteProfileModalContext = createContext(null);

export function CompleteProfileModalProvider({ children }) {
  const [request, setRequest] = useState(null);

  const ensureProfileComplete = useCallback((user) => {
    if (isUserProfileComplete(user)) {
      return Promise.resolve(user);
    }
    return new Promise((resolve, reject) => {
      setRequest({ user, resolve, reject });
    });
  }, []);

  useEffect(() => {
    registerProfileCompleteHandler(ensureProfileComplete);
    return () => registerProfileCompleteHandler(null);
  }, [ensureProfileComplete]);

  const handleComplete = (updatedUser) => {
    request?.resolve(updatedUser);
    setRequest(null);
  };

  const handleCancel = () => {
    request?.reject(new Error('Profile completion cancelled'));
    setRequest(null);
  };

  return (
    <CompleteProfileModalContext.Provider value={{ ensureProfileComplete }}>
      {children}
      {request ? (
        <Suspense fallback={null}>
          <CompleteProfileModal
            user={request.user}
            onComplete={handleComplete}
            onCancel={handleCancel}
          />
        </Suspense>
      ) : null}
    </CompleteProfileModalContext.Provider>
  );
}

export function useCompleteProfileModal() {
  const ctx = useContext(CompleteProfileModalContext);
  if (!ctx) {
    throw new Error('useCompleteProfileModal must be used within CompleteProfileModalProvider');
  }
  return ctx;
}
